import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnim, V } from '../hooks/useScrollAnimation';
import {
  FiPackage, FiCoffee, FiTruck, FiBriefcase,
} from 'react-icons/fi';
import {
  FaUtensils, FaStore, FaBoxes, FaShoppingCart,
  FaHotel, FaIndustry, FaCloud, FaBuilding,
} from 'react-icons/fa';

const categories = [
  {
    label: "PACKAGING DIVISION",
    icon: FiPackage,
    title: "Food Packaging",
    desc: "Containers, bowls, meal trays and storage solutions engineered for high-volume food service operations.",
    features: ["Restaurants", "Cloud Kitchens", "Food Chains"],
    buttonText: "Explore Solutions",
    buttonLink: "/products?category=food",
    color: "#E91E8C",
    accent: "rgba(233,30,140,0.08)",
  },
  {
    label: "PACKAGING DIVISION",
    icon: FiCoffee,
    title: "Beverage Packaging",
    desc: "Premium disposable cups, lids and beverage packaging for cafés, QSRs and hospitality.",
    features: ["Cafés", "Food Chains", "Catering"],
    buttonText: "Explore Solutions",
    buttonLink: "/products?category=beverage",
    color: "#FF6B2B",
    accent: "rgba(255,107,43,0.08)",
  },
  {
    label: "SUPPLY DIVISION",
    icon: FiTruck,
    title: "Retail & Distribution",
    desc: "Robust packaging for wholesalers, distributors and modern retail supply networks.",
    features: ["Wholesale", "Distribution", "Retail Chains"],
    buttonText: "Explore Solutions",
    buttonLink: "/products?category=retail",
    color: "#6B21A8",
    accent: "rgba(107,33,168,0.08)",
  },
  {
    label: "SUPPLY DIVISION",
    icon: FiBriefcase,
    title: "Custom Supply",
    desc: "Tailored sourcing, bulk procurement and custom packaging programs for your exact needs.",
    features: ["Custom Sizes", "Private Label", "Dedicated Support"],
    buttonText: "Request Consultation",
    buttonLink: "https://wa.me/919821991932?text=Hi, I need custom packaging solutions.",
    color: "#E91E8C",
    accent: "rgba(233,30,140,0.08)",
  },
];

const industries = [
  { icon: FaUtensils,    name: "Food Service" },
  { icon: FaStore,       name: "Retail" },
  { icon: FaBoxes,       name: "Distribution" },
  { icon: FaShoppingCart,name: "eCommerce" },
  { icon: FaHotel,       name: "Hospitality" },
  { icon: FaIndustry,    name: "Manufacturing" },
  { icon: FaCloud,       name: "Cloud Kitchens" },
  { icon: FaBuilding,    name: "Corporate" },
];

const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 40 },
  whileInView:{ opacity: 1, y: 0 },
  viewport:   { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function ProductPortfolio() {
  const { ref, controls } = useScrollAnim(0.05);

  return (
    <section
      id="portfolio"
      style={{
        padding: 'clamp(60px,8vh,100px) 0',
        background: 'linear-gradient(180deg,#fff 0%,#fff5f9 55%,#fffbf5 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* bg radials */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(circle at 10% 20%,rgba(233,30,140,0.05) 0%,transparent 55%),
                     radial-gradient(circle at 90% 70%,rgba(255,107,43,0.05) 0%,transparent 55%)`,
      }} />

      <div className="container" ref={ref} style={{ position: 'relative', padding: '0 20px' }}>

        {/* ── Section header ── */}
        <motion.div {...fadeUp(0)} style={{ textAlign: 'center', marginBottom: 'clamp(36px,6vh,60px)' }}>
          <span style={{
            display: 'inline-block', padding: '5px 16px', borderRadius: 999,
            background: 'rgba(233,30,140,0.08)', color: '#E91E8C',
            fontWeight: 700, fontSize: '0.73rem', letterSpacing: '0.14em',
            marginBottom: '1rem',
          }}>
            OUR PRODUCT PORTFOLIO
          </span>

          <h2 style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'clamp(1.9rem,4vw,3.2rem)', lineHeight: 1.1,
            marginBottom: '1rem',
          }}>
            Packaging &amp; Supply Solutions
            <br />
            <span style={{
              background: 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Designed For Business Growth
            </span>
          </h2>

          <p style={{
            color: 'var(--text-muted)', fontSize: 'clamp(0.88rem,1.3vw,1rem)',
            maxWidth: 640, margin: '0 auto', lineHeight: 1.7,
          }}>
            Comprehensive packaging solutions engineered to support modern businesses across every industry.
          </p>
        </motion.div>

        {/* ── Cards: 2-col on desktop, 1-col on mobile ── */}
        <div className="portfolio-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 'clamp(16px,2.5vw,28px)',
          marginBottom: 'clamp(48px,8vh,80px)',
        }}>
          {categories.map((cat, i) => (
            <motion.div
              key={i}
              {...fadeUp(i * 0.08)}
              whileHover={{ y: -8, boxShadow: `0 24px 56px rgba(233,30,140,0.13)` }}
              style={{
                background: '#fff',
                borderRadius: 24,
                border: '1.5px solid rgba(233,30,140,0.09)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.35s, transform 0.35s',
              }}
            >
              {/* Card top accent bar */}
              <div style={{
                height: 4,
                background: `linear-gradient(90deg,${cat.color},#FF6B2B)`,
              }} />

              <div style={{ padding: 'clamp(20px,3vw,32px)', display: 'flex', flexDirection: 'column', flex: 1 }}>

                {/* Icon + label row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 14,
                    background: cat.accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <cat.icon size={22} color={cat.color} />
                  </div>
                  <span style={{
                    fontSize: '0.68rem', fontWeight: 700,
                    letterSpacing: '0.12em', color: cat.color,
                  }}>
                    {cat.label}
                  </span>
                </div>

                {/* Title */}
                <h3 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(1.1rem,2vw,1.45rem)',
                  fontWeight: 700, lineHeight: 1.25,
                  marginBottom: 10,
                }}>
                  {cat.title}
                </h3>

                {/* Description */}
                <p style={{
                  color: 'var(--text-muted)', lineHeight: 1.65,
                  fontSize: 'clamp(0.82rem,1.1vw,0.92rem)',
                  marginBottom: 16, flex: 1,
                }}>
                  {cat.desc}
                </p>

                {/* Feature pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 8px', marginBottom: 20 }}>
                  {cat.features.map((f, fi) => (
                    <span key={fi} style={{
                      padding: '4px 12px', borderRadius: 99,
                      background: cat.accent,
                      color: cat.color,
                      fontSize: '0.75rem', fontWeight: 600,
                    }}>
                      {f}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <motion.a
                  href={cat.buttonLink}
                  target={cat.buttonLink.startsWith('http') ? '_blank' : undefined}
                  rel="noreferrer"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0.75rem 1.5rem',
                    background: `linear-gradient(135deg,${cat.color},#FF6B2B)`,
                    color: '#fff', borderRadius: 12,
                    fontWeight: 700, textDecoration: 'none',
                    fontSize: 'clamp(0.8rem,1vw,0.9rem)',
                    boxShadow: `0 8px 24px ${cat.color}30`,
                    alignSelf: 'flex-start',
                  }}
                >
                  {cat.buttonText} →
                </motion.a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── Industries We Serve ── */}
        <motion.div {...fadeUp(0.1)} style={{ textAlign: 'center', marginBottom: 'clamp(28px,4vh,44px)' }}>
          <span style={{
            display: 'inline-block', padding: '5px 16px', borderRadius: 999,
            background: 'rgba(233,30,140,0.08)', color: '#E91E8C',
            fontWeight: 700, fontSize: '0.73rem', letterSpacing: '0.14em',
            marginBottom: '0.9rem',
          }}>
            WHO WE SERVE
          </span>
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.5rem,3vw,2.2rem)',
            fontWeight: 700, lineHeight: 1.15,
          }}>
            Industries We Serve
          </h3>
        </motion.div>

        {/* Industry grid — 4 col desktop, 2 col tablet, 2 col mobile */}
        <div className="industry-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 'clamp(10px,1.8vw,18px)',
        }}>
          {industries.map((ind, i) => (
            <motion.div
              key={i}
              {...fadeUp(i * 0.05)}
              whileHover={{ y: -5, boxShadow: '0 16px 40px rgba(233,30,140,0.1)' }}
              style={{
                background: '#fff',
                padding: 'clamp(16px,2.5vw,24px) clamp(12px,2vw,20px)',
                borderRadius: 18,
                border: '1.5px solid rgba(233,30,140,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.65rem',
                cursor: 'default',
                transition: 'all 0.3s ease',
              }}
            >
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: 'rgba(233,30,140,0.07)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ind.icon size={22} color="#E91E8C" />
              </div>
              <span style={{
                fontWeight: 600,
                fontSize: 'clamp(0.78rem,1.1vw,0.92rem)',
                color: 'var(--text)',
                textAlign: 'center',
                lineHeight: 1.3,
              }}>
                {ind.name}
              </span>
            </motion.div>
          ))}
        </div>

      </div>

      <style>{`
        /* Tablet: 2-col industry grid */
        @media (max-width: 900px) {
          .industry-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }

        /* Mobile: 1-col cards, 2-col industries */
        @media (max-width: 640px) {
          .portfolio-grid {
            grid-template-columns: 1fr !important;
          }
          .industry-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        /* Very small */
        @media (max-width: 380px) {
          .industry-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 8px !important;
          }
        }
      `}</style>
    </section>
  );
}